import Button from "../../../MentorDashboardV2/UI/Buttons";
import { SvgIcons } from "../../../SharedComponents/SvgIcons/SvgIcons";
import SprHistoryModal from "./SprHistoryModal";
import useSprStatusBlock from "./useSprStatusBlock";
import styles from "./style.module.css";

const OpenInNewIcon = () => <SvgIcons name="open_in_new" width="16" height="16" />;

/**
 * "Student Performance Report" entry block (SPR v2). Shows which versions exist for the
 * student + course and opens the review page on the automated report or the saved draft.
 * Its data is the `spr` summary on the host's course row (see `useSprStatusBlock`).
 * When something was sent, a "Last sent to parent" line with View history (SPR History popup).
 *
 * @param {string} studentId
 * @param {string} courseId - selected course, owned by the host screen
 * @param {string} [courseSection] - that course row's courseSection (SPR History needs it)
 * @param {Object|null} [spr] - that course row's `spr` summary: absent = SPR off for this user
 *   (renders nothing), null = no SPR for this subject, object = the versions to show
 * @param {Array<{value: string, text: string}>} [courses] - when given, a Course dropdown
 *   is rendered inside the block (LM profile screen); its changes go to `onCourseChange`
 * @param {Function} [onCourseChange] - the host's handler for that dropdown
 * @param {boolean} [canEdit] - the viewer can open the review page (sprReview permission:
 *   admin, mentor, Moderator). Other roles see the block with both buttons disabled.
 */
const SprStatusBlock = ({
  studentId,
  courseId,
  courseSection,
  spr,
  courses,
  onCourseChange,
  canEdit = false,
}) => {
  const { state, derived, handlers } = useSprStatusBlock({ studentId, courseId, spr });
  const { summary, isHistoryOpen } = state;
  if (derived.hasCourse && derived.isFeatureOff) return null;

  return (
    <section className={styles.block} aria-label="Student Performance Report">
      <h3 className={styles.heading}>Student Performance Report</h3>

      {courses && (
        <label className={styles.courseField}>
          <span className={styles.courseLabel}>Course</span>
          <select
            className={styles.courseSelect}
            value={courseId ?? ""}
            onChange={onCourseChange}
          >
            <option value="" disabled>
              Select course
            </option>
            {courses?.map((course) => (
              <option key={course?.value} value={course?.value}>
                {course?.text}
              </option>
            ))}
          </select>
        </label>
      )}

      {!derived.hasCourse && (
        <p className={styles.hint}>Select a course to see its progress report.</p>
      )}

      {derived.hasCourse && derived.hasNoReport && (
        <p className={styles.hint}>There's no progress report for this course's subject.</p>
      )}

      {derived.hasCourse && summary && (
        <>
          <div className={styles.cards}>
            <div
              className={`${styles.card} ${summary.automated?.generatedAtLabel ? "" : styles.cardNoDate}`}
            >
              <div className={styles.cardTitle}>Automated version</div>
              {/* The label introduces the date, so both show only when BE sends one. */}
              {summary.automated?.generatedAtLabel && (
                <>
                  <p className={styles.cardText}>Last report made from student data</p>
                  <p className={styles.cardDate}>{summary.automated.generatedAtLabel}</p>
                </>
              )}
              <Button
                variant="outline"
                color="primary"
                radius="soft"
                size="sm"
                className={styles.openButton}
                endIcon={<OpenInNewIcon />}
                disabled={!canEdit}
                onClick={handlers.onReviewAutomated}
              >
                Review and edit
              </Button>
            </div>

            {summary.draft && (
              <div
                className={`${styles.card} ${summary.draft?.savedAtLabel ? "" : styles.cardNoDate}`}
              >
                <div className={styles.cardTitle}>Last saved version</div>
                {summary.draft?.savedAtLabel && (
                  <>
                    <p className={styles.cardText}>Your last manual changes saved on</p>
                    <p className={styles.cardDate}>{summary.draft.savedAtLabel}</p>
                  </>
                )}
                <Button
                  variant="outline"
                  color="primary"
                  radius="soft"
                  size="sm"
                  className={styles.openButton}
                  endIcon={<OpenInNewIcon />}
                  disabled={!canEdit}
                  onClick={handlers.onContinueDraft}
                >
                  Continue editing
                </Button>
              </div>
            )}
          </div>

          {!canEdit && (
            <p className={styles.hint}>Only admins, mentors and moderators can review the report.</p>
          )}

          {summary.lastSent && (
            <div className={styles.lastSent}>
              <SvgIcons name="history_icon" width="16" height="16" />
              <span>Last sent to parent:</span>
              <strong>{summary.lastSent?.sentAtLabel}</strong>
              <button type="button" className={styles.historyLink} onClick={handlers.onOpenHistory}>
                View history
                <OpenInNewIcon />
              </button>
            </div>
          )}
        </>
      )}

      {isHistoryOpen && (
        <SprHistoryModal
          studentId={studentId}
          courseSection={courseSection}
          subjectKey={summary?.subjectKey}
          onPreview={handlers.onPreviewSent}
          onClose={handlers.onCloseHistory}
        />
      )}
    </section>
  );
};

export default SprStatusBlock;
